<template>
  <div>
    <div class="container mb-md-7 pb-md-3">
      <breadcrumb :product="product" class="-product" />
    </div>
    <section class="o-product-detail">
      <div v-if="product" class="container">
        <div class="row">
          <!-- Gallery -->
          <div class="col-md-7 col-lg-8 o-product-detail__info">
            <product-gallery
              v-if="product.images"
              :product="product"
              :images="product.images"
              :videos="videos"
              :is-loading="isLoading"
              :product-sku="product.sku"
              :is-product-on-discount="isProductOnDiscount"
            />
            <!-- Description -->
            <div v-if="$isNotNullOrUndefined(product.longDescription)" class="o-product-detail__long-description mt-8">
              <hr />
              <accordion
                  :title="$t('productDescription', 'Produkt beskrivelse').toUpperCase()"
                  :show-chevron="true"
                  :hide-content-desktop="true"
                  class="my-6"
              >
                <div v-html="product.longDescription.replace(/\n/g, '<br>')" />
              </accordion>
            </div>
            <hr v-if="$isNotNullOrUndefined(product.longDescription) || ($isNotNullOrUndefined(properties) && properties.length > 0)" />
            <!-- Product information -->
            <div v-if="$isNotNullOrUndefined(properties) && properties.length > 0" class="my-6">
              <accordion :title="$t('productInfo', 'Produktinformation').toUpperCase()" :show-chevron="true" :hide-content-desktop="true">
                <div v-for="(prop, index) in properties" :key="index">
                  <div class="spec__item pt-2 pb-2">
                    {{ prop.name }}: {{ prop.value }}
                    {{ $isNotNullOrUndefined(prop.unit) }}
                  </div>
                </div>
              </accordion>
            </div>
            <hr
                v-if="
                ($isNotNullOrUndefined(properties) && properties.length > 0) ||
                ($isNotNullOrUndefined(filteredDownloads) && filteredDownloads.length > 0)
              "
            />
            <!-- Downloads -->
            <div v-if="$isNotNullOrUndefined(filteredDownloads) && filteredDownloads.length > 0" class="o-product-detail__accessories-box">
              <accordion :title="$t('downloads', 'Downloads').toUpperCase()" :show-chevron="true" :hide-content-desktop="true" class="my-6">
                <ul class="mb-0 p-0 row list-unstyled">
                  <li v-for="(file, index) in filteredDownloads" :key="index" class="col-12 my-2">
                    <a :href="file.link" class="d-flex text-body" target="_blank">
                      <base-icon class="my-auto mr-3 text-primary" icon="chevron-right" />
                      {{ $t(`Product${file.name}`, file.name) }}
                    </a>
                  </li>
                </ul>
              </accordion>
              <hr />
            </div>
          </div>

          <!-- Purchase controls -->
          <div class="col-md-5 col-lg-4">
            <div class="row justify-content-end">
              <div class="col-md-11 col-xl-10">
                <div v-if="brandLogo" class="o-product-detail__brand-logo mb-2">
                  <base-image :url="brandLogo.link" :width="90" :height="45" :is-lazy-load="false" :responsive="false" mode="pad" />
                </div>
                <h1>
                  {{ product.title }}
                </h1>
                <div class="d-flex mt-4">
                  <div class="d-flex flex-column flex-grow-1">
                    <div class="d-flex flex-column mb-4">
                      <span>
                        {{ $isNotNullOrUndefined(getPropertyValue('Description2')) ? getPropertyValue('Description2') : '' }}
                      </span>
                      <span>
                        {{ $isNotNullOrUndefined(getPropertyValue('Description3')) ? getPropertyValue('Description3') : '' }}
                      </span>
                    </div>
                    <div class="o-product-detail__sku">
                      <span v-if="$isNotNullOrUndefined(product.sku)" class="mr-2">
                        {{ $t('productId', 'Varenr.') }}
                        <clip-board>{{ product.sku }}</clip-board>
                      </span>
                      <template v-if="$isNotNullOrUndefined(product) && $isNotNullOrUndefined(product.model)">
                        <span>//</span>
                        <span class="ml-2"> {{ $t('productModel', 'Model') }} {{ product.model }} </span>
                      </template>
                    </div>
                    <div class="o-product-detail__kolli">
                      {{ $t('kolliNumber', 'Antal pr. kolli') }}
                      {{ kolliNumber }}
                    </div>
                  </div>
                  <div class="d-flex flex-column">
                    <div v-if="energyLabel" class="o-product-detail__energy-label">
                      <base-link class="o-product-detail__energy-label-link -image" :link="energyLabelDownload.link" target="_blank">
                        <base-image
                          :url="`/energy-labels/${energyLabel.toLowerCase().replace(/\+/g, 'p')}.png`"
                          :width="65"
                          :height="50"
                          :include-cdn="false"
                          :is-lazy-load="false"
                        />
                      </base-link>
                      <base-link
                        v-if="datasheetDownload.link"
                        class="o-product-detail__energy-label-link -datasheet"
                        :link="datasheetDownload.link"
                        target="_blank"
                      >
                        {{ $t('ProductDatasheet') }}
                      </base-link>
                    </div>
                  </div>
                </div>
                <usps v-if="$isNotNullOrUndefined(product.usps)" :usps="product.usps" :class="'font-weight-bold'" />
                <div class="d-flex flex-row justify-content-between">
                  <div v-if="product.displayOnSite !== false">
                    <price
                      v-if="product.price && product.price.price > 0"
                      class="lead mt-4 mt-md-5 d-inline-block"
                      :price="product.price"
                      :product="product"
                      :is-logged-in="isLoggedIn"
                      :is-product-on-discount="isProductOnDiscount"
                      :is-lavpris="!isLoggedIn && advertisingCode && advertisingCode === 'LAVPRIS'"
                    />
                  </div>
                </div>
                <div v-if="isRestSalg" class="o-product-detail__while-available mt-4">
                  {{ $t('ProductCard_AvailableWhileInStock', 'Så længe laver haves') }}
                </div>
                <product-variants v-if="!brandPriceHidden()" :sku="product.sku" :variants="product.variants" />
                <div v-if="!brandPriceHidden() && allowPurchase" class="d-flex align-items-center mt-6">
                  <quantity-input
                    :id="product.id"
                    :quantity="quantity"
                    class="mr-4"
                    :is-large="true"
                    :min-quantity="kolliNumber"
                    :max-quantity="product.stockInfo?.actualAmount"
                    :only-stock-quantity="isRestSalg"
                    :kolli="kolliNumber"
                    @quantity-change="onQuantityChange"
                    @show-stock-warning="checkInputValue"
                  />
                  <base-button class="-primary -no-outline w-100" @click.native="onAddToCart">
                    <base-loader v-if="showLoader" />
                    <base-icon v-else-if="showTick" class="margin-0-auto text-white" icon="check" />
                    <span v-else>{{ $t('AddToBasket', 'Læg i kurv') }}</span>
                  </base-button>
                </div>
                <login-user v-else :is-logged-in="isLoggedIn"></login-user>

                <div class="pt-2">
                  <span v-if="showStockWarning" class="o-product-detail__stock-warning">
                    {{ stockWarningMessage }}
                  </span>
                </div>
                <div class="mt-6">
                  <stock-status
                    v-if="product.sku"
                    :sku="product.sku"
                    :delivery-time="deliveryTime"
                    :is-ordering-item="isOrderingItem"
                    :allow-to-subscribe="true"
                    :stock="product.stockInfo"
                  />
                </div>
                <div v-if="showLeasingCalculator" ref="scriptContainer" class="mt-8" />
              </div>
            </div>
            <!-- Bronnum Care -->
            <div v-if="isBronnumCareProduct" class="row justify-content-end">
              <div class="col-4 col-md-5 col-xl-4">
                <base-link :link="bronnumCarePage ? bronnumCarePage.link.url : null" class="unstyled-link">
                  <base-icon icon="care-icon" class="o-product-detail__care-icon w-100 h-auto p-3" />
                </base-link>
              </div>

              <div class="col-8 col-md-6 col-xl-6 my-auto">
                <div class="bold text-small">
                  {{ $t('bronnumCareBox', 'Brønnum Care produkt') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Cross Selling & Alternative & Complimentary -->
        <div class="row mt-8 mt-lg-10 mt-xl-12">
          <div class="col-12 mt-8 mt-md-1">
            <div class="row">
              <div class="col-12 o-product-detail__slider-mask pr-0">
                <div class="row mr-0">
                  <client-only>
                    <template v-if="$isNotNullOrUndefined(crossSellingProducts) && crossSellingProducts.length > 0">
                      <product-slider
                        :headline="$t('crossSellingProducts', 'Relaterede produkter')"
                        :list-name="'Alternatives to - ' + product.sku"
                        :products="crossSellingProducts"
                        :is-loading="crossSellingIsloading"
                      />
                    </template>
                    <template v-if="$isNotNullOrUndefined(substitutingProducts) && substitutingProducts.length > 0">
                      <product-slider
                        :headline="$t('productSuggestions', 'Andre kiggede også på')"
                        :list-name="'Alternatives to - ' + product.sku"
                        :products="substitutingProducts"
                        :is-loading="substitutingIsloading"
                      />
                    </template>
                    <template v-if="$isNotNullOrUndefined(complementaryProducts) && complementaryProducts.length > 0">
                      <product-slider
                        :headline="$t('accessories', 'Tilbehør')"
                        :list-name="'Alternatives to - ' + product.sku"
                        :products="complementaryProducts"
                        :is-loading="complementaryIsloading"
                        class="pb-5"
                      />
                    </template>
                  </client-only>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section
      v-for="(contentElement, index) in content.nestedContent"
      :key="index"
      :class="getElementTemplate(contentElement) ? getSpacingTopClass(contentElement) + ' ' + getSpacingBottomClass(contentElement) : null"
    >
      <component :is="getElementTemplate(contentElement)" v-if="getElementTemplate(contentElement)" :content="contentElement" />
    </section>
  </div>
</template>

<script>
import ClipBoard from '@/components/atoms/ClipBoard'
import Accordion from '@/components/molecules/Accordion.vue'
import Badge from '@/components/atoms/Badge.vue'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseLoader from '@/components/atoms/BaseLoader.vue'
import BaseSelect from '@/components/atoms/BaseSelect.vue'
import QuantityInput from '@/components/molecules/QuantityInput.vue'
import Price from '@/components/atoms/Price.vue'
import ProductVariants from '@/components/molecules/ShopCommerce/ProductVariants'
import Usps from '@/components/atoms/Usps.vue'
import ProductGallery from '@/components/molecules/ProductGallery.vue'
import Tabs from '@/components/molecules/Tabs.vue'
import Tab from '@/components/atoms/Tab.vue'
import contentMixin from '@/mixins/contentMixin'
import LoginMixin from '@/mixins/loginMixin'
import breadcrumb from '@/components/molecules/Breadcrumb.vue'
import StockStatus from '@/components/molecules/ShopCommerce/StockStatus.vue'
import BaseIcon from '@/components/atoms/BaseIcon'
import ProductSlider from '@/components/organisms/ProductSlider'

import { trackProductView, trackAddToCart } from '@/utils/enhancedEcomTracking'
import { mapViewedProduct } from '@/utils/klaviyoTracking'
import { getKolliNumber, getEnergyLabel } from '@/utils/ShopCommerce/product-properties'
import BaseImage from '@/components/atoms/BaseImage'
import BaseLink from '@/components/atoms/BaseLink'
import LoginUser from '~/components/molecules/LoginUser.vue'

export default {
  name: 'ProductDetail',
  components: {
    LoginUser,
    ClipBoard,
    ProductGallery,
    Price,
    ProductVariants,
    Usps,
    QuantityInput,
    BaseButton,
    BaseSelect,
    BaseLoader,
    Tabs,
    Tab,
    Badge,
    Accordion,
    breadcrumb,
    StockStatus,
    BaseIcon,
    BaseImage,
    BaseLink,
    ProductSlider
  },
  mixins: [contentMixin, LoginMixin],
  async fetch() {
    try {
      const product = await this.$repositories.product.byGuid(this.currentCulture, this.guid)
      let productAssets = await this.$repositories.assets.bySkus([product?.sku])
      let productAsset = productAssets ? productAssets[0] : null

      // Add images to product object
      let packshots = productAsset?.data?.pack
        ? productAsset?.data?.pack?.map((shot) => {
            return {
              name: product.title,
              url: `/kontainer/${process.env.kontainerUrl}/${shot.url}`
            }
          })
        : []
      let images = productAsset?.data?.images
        ? productAsset?.data?.images?.map((image) => {
            return {
              name: product.title,
              url: `/kontainer/${process.env.kontainerUrl}/${image.url}`
            }
          })
        : []
      product.images = [...packshots, ...images]

      const brandlogo = productAsset?.data?.files
        ? productAsset?.data?.files?.map((file) => {
            return {
              name: file.name,
              link: `/kontainer/${process.env.kontainerUrl}/${file.url}`
            }
          })
        : []
      product.brandlogo = brandlogo
      // Add files to product object
      const downloads = productAsset?.data?.files
        ? productAsset?.data?.files?.map((file) => {
            return {
              name: file.name,
              link: `${process.env.kontainerUrl}${file.url}`
            }
          })
        : []
      product.downloads = downloads

      // Assign once, with images/brandlogo/downloads already attached, so the product() watcher
      // (which fires the Klaviyo "Viewed Product" event) sees the real packshot image URLs and
      // doesn't fall back to the SKU-guessed .png.
      this.product = product

      this.quantity = this.kolliNumber
      this.isProductOnDiscount = this.isPriceOnDiscount(this.product.price)
    } catch {
      console.log('api error in $repositories.product.byGuid, guid: ' + this.guid)
    } finally {
      this.isLoading = false
    }
  },
  data() {
    return {
      isLoading: true,
      product: null,
      quantity: 1,
      videos: [],
      complimentaryTriggered: false,
      complementaryIsloading: false,
      complementaryProducts: [],
      crossSellingTriggered: false,
      crossSellingIsloading: false,
      crossSellingProducts: [],
      substitutingTriggered: false,
      substitutingIsloading: false,
      substitutingProducts: [],
      showLoader: false,
      showTick: false,
      trackingTriggered: false,
      isProductOnDiscount: false,
      open: [],
      items: [],
      showStockWarning: false,
      excessQuantity: 0
    }
  },
  computed: {
    allowPurchase() {
      return (this.product.price && this.product.price.price > 0) || this.isVivaldiUser
    },
    guid() {
      return this.$store.getters['content/content/getContent']?.meta?.id
    },
    currentCulture() {
      return this.$store.getters['site/getCurrentCulture']
    },
    siteSettings() {
      return this.$store.getters["site/getSiteSettings"];
    },
    currency() {
      return this.$store.getters['currency/getCurrentCurrency']
    },
    properties() {
      // Add sortOrder to properties
      let contentProperties = this.content?.properties?.map((prop, index) => {
        return { ...prop, sortOrder: index }
      })
      // Only display properties listed in backoffice
      let productPropertiesFiltered = this.product?.properties?.filter((productProp) => {
        return contentProperties?.some((contentProp) => {
          return contentProp.name.toLowerCase() === productProp.name.toLowerCase() && productProp?.value?.length
        })
      })
      // Use translation from the list in backoffice - and the value from product property
      let propertiesMapped = productPropertiesFiltered?.map((productProp) => {
        let prop = contentProperties?.find((contentProp) => contentProp.name.toLowerCase() === productProp.name.toLowerCase())
        return {
          name: prop.value,
          value: productProp.value,
          sortOrder: prop.sortOrder,
          unit: productProp.unit
        }
      })

      /*
        Hotfix: some products seem to be importet with several dublicate properties. 
        This line "uniqueProperties" ensures that we remove all dublicates before presenting the array to the frontend
        - MRA
      */

      /*       let uniqueProperties = [...new Map(propertiesMapped.map((x) => [x.name, x])).values()];
            uniqueProperties?.sort((a, b) => this.sortByValue(a, b))
            return uniqueProperties; */

      propertiesMapped?.sort((a, b) => this.sortByValue(a, b))
      return propertiesMapped
    },
    kolliNumber() {
      return !this.product ? 1 : getKolliNumber(this.product)
    },
    energyLabel() {
      return getEnergyLabel(this.product)
    },
    energyLabelDownload() {
      return this.product.downloads?.find((download) => download.name === 'Energimærkning') ?? {}
    },
    datasheetDownload() {
      return this.product.downloads?.find((download) => download.name === 'Datasheet') ?? {}
    },
    brandLogo() {
      return this.product.brandlogo?.find((logo) => logo.name === 'Logo')
    },
    filteredDownloads() {
      const excludedDownloads = ['Logo']
      return this.product.downloads?.filter((download) => !excludedDownloads.includes(download.name)) ?? []
    },
    isVivaldiUser() {
      return this.user?.accountProperties?.vivaldi === 1
    },
    crossSellingProductIds() {
      return this.product.properties?.find((property) => property.name === 'CrossSellingItems')?.value.replace(/;/g, ',') ?? []
    },
    advertisingCode() {
      return this.getPropertyValue('AdvertisingCode')
    },
    isRestSalg() {
      return (this.advertisingCode && this.advertisingCode === 'RESTSALG') ?? false
    },
    showAdvertisingCode() {
      return this.advertisingCode &&
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== `ProductCard_Discount_${this.advertisingCode}` &&
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== ''
        ? true
        : false
    },
    isBronnumCareProduct() {
      return this.product.properties.filter((x) => x.name === 'Broennum_Care' && x.value === 'Ja').length > 0
    },
    bronnumCarePage() {
      return this.$store.getters['navigation/getContentNavigation'].children?.find((x) => x?.link?.name === 'Brønnum Care')
    },
    deliveryTime() {
      return parseFloat(this.getPropertyValue('DeliveryTimeOutOfStock'))
    },
    isOrderingItem() {
      return this.getPropertyValue('OrderingItem') === 'TRUE'
    },
    stockWarningMessage() {
      let message = this.$t('StockWarningMessage')
      // find a nummeral in this string and replace it with this.product.stockInfo?.actualAmount
      return message.replace(/\d+/, this.product.stockInfo?.actualAmount)
    },
    customProperties() {
      return {
        orderingItem: this.isOrderingItem
      }
    },
    showLeasingCalculator() {
      return this.product?.price?.regularExVat > 15000
    },
    productCategoryPath() {
      const path = this.findCategoryPath(this.$store.getters['navigation/getNavigationItems'], this.product?.primaryCategoryId)
      return path?.map((item) => item.link?.name).filter(Boolean) ?? null
    }
  },
  watch: {
    product() {
      if (this.$isNotNullOrUndefined(this.product)) {
        if (this.complimentaryTriggered === false) {
          this.complimentaryTriggered = true
          this.getComplementaryProducts()
        }

        if (this.substitutingTriggered === false) {
          this.substitutingTriggered = true
          this.getSubstitutingProducts()
        }

        if (this.crossSellingTriggered === false) {
          this.crossSellingTriggered = true
          this.getCrossSellingProducts()
        }

        // Klaviyo tracking
        if (this.trackingTriggered === false) {
          this.trackingTriggered = true
          trackProductView(this.product, this.product?.primaryCategoryName)
          const email = this.$store.getters['user/user/getUser']?.email || this.$store.getters['cart/basket/getBasket']?.billingAddress?.email || null
          this.$repositories.klaviyo.track('Viewed Product', mapViewedProduct(this.product, this.productCategoryPath), email, null)
        }
      }
    }
  },
  mounted() {
    // delay the trackingTriggered to ensure that the product is loaded
    setTimeout(() => {
      // Dynamically insert the script when the component is mounted
      const script = document.createElement('script')
      script.id = 'ldScriptLoader'
      script.src = 'https://leaddoubler.s3.eu-west-1.amazonaws.com/files/ldScriptLoader.js'
      script.setAttribute('iframesrc', 'https://bronnumleasing.beregner.net')
      script.setAttribute('loadParams', 'true')
      script.setAttribute('urlCheck', '')
      script.setAttribute('autoScroll', 'false')
      script.setAttribute('offset', '0')
      // Append the script to the container element with the ref attribute
      if (this.$refs.scriptContainer) {
        // Create a new URLSearchParams object
        const queryParams = new URLSearchParams()
        const productUrl = this.product.url.split('/').pop()
        const productPrice = this.isProductOnDiscount ? this.product.price?.priceExVat : this.product.price?.regularExVat
        const productFullUrl = window.location.origin + this.product.url.split('?')[0] // Remove query string from the URL

        // Append parameters to the query string
        queryParams.set('product', productUrl)
        queryParams.set('productPrice', productPrice)
        queryParams.set('productURL', productFullUrl)

        // Update the URL without causing a refresh
        const newUrl = window.location.pathname + '?' + queryParams.toString()
        window.history.replaceState(null, '', newUrl)

        this.$refs.scriptContainer.appendChild(script)
      }

      // Only expose schema if there is no user logged in
      if (!this.isLoggedIn) {
        this.setSchema()
      }
    }, 1000)
  },
  methods: {
    findCategoryPath(array, id) {
      if (typeof array !== 'undefined' && array && id) {
        for (let i = 0; i < array.length; i++) {
          if (array[i].id == id) return [array[i]]
          const path = this.findCategoryPath(array[i].children, id)
          if (path != null) {
            path.unshift(array[i])
            return path
          }
        }
      }
      return null
    },
    checkInputValue(value) {
      return (this.showStockWarning = value)
    },
    sortByValue(a, b) {
      return a.sortOrder - b.sortOrder
    },
    async getComplementaryProducts() {
      this.complementaryIsloading = true
      try {
        const data = await this.$repositories.products.byComplimentary(this.currentCulture, this.product.sku, 10, 1)
        this.complementaryProducts = await this.$kontainer.getPackshots(data?.items)
        this.complementaryIsloading = false
      } catch {
        console.log('api error in $repositories.products.byComplimentary')
      }
    },
    async getSubstitutingProducts() {
      this.substitutingIsloading = true
      try {
        const data = await this.$repositories.products.bySubstitutions(this.currentCulture, this.product.sku, 10, 1)
        this.substitutingProducts = await this.$kontainer.getPackshots(data?.items)
        this.substitutingIsloading = false
      } catch {
        console.log('api error in $repositories.products.bySubstitutions')
      }
    },
    async getCrossSellingProducts() {
      if (this.crossSellingProductIds.length > 0) {
        this.crossSellingIsloading = true
        try {
          const data = await this.$repositories.products.byIds(this.currentCulture, this.crossSellingProductIds)
          this.crossSellingProducts = await this.$kontainer.getPackshots(data)
          this.crossSellingIsloading = false
        } catch {
          console.log('failed to fetch crossSellingProducts - api error in $repositories.products.fetchProductsByIds')
        }
      }
    },
    onQuantityChange(payload) {
      this.quantity = payload.quantity
    },
    async onAddToCart() {
      this.showLoader = true

      // if ordered more then in the stock, send 2 separate requests
      if (this.product.stockInfo?.actualAmount != 0 && this.product.stockInfo?.actualAmount < this.quantity) {
        const kolliInStock = Math.floor(this.product.stockInfo?.actualAmount / this.kolliNumber)
        const totalStockItems = kolliInStock * this.kolliNumber
        this.excessQuantity = this.quantity - totalStockItems

        if (this.excessQuantity > 0) {
          // add message in the basket
          this.customProperties.outOfStock = true

          await this.$store.dispatch('cart/basket/addToBasket', {
            culture: this.currentCulture,
            sku: this.product?.sku,
            currency: this.currency,
            quantity: this.excessQuantity,
            properties: this.customProperties
          })
        }
      }

      this.customProperties.outOfStock = false

      await this.$store.dispatch('cart/basket/addToBasket', {
        culture: this.currentCulture,
        sku: this.product?.sku,
        currency: this.currency,
        quantity: this.quantity - this.excessQuantity,
        properties: this.customProperties
      })
      this.showTick = true
      this.showLoader = false
      setTimeout(() => {
        this.showTick = false
      }, 2000)

      trackAddToCart(this.product, this.quantity, this.currency)
    },
    isPriceOnDiscount(price) {
      return price?.price < price?.regular
    },
    getPropertyValue(propertyName) {
      let item = this.product.properties.find((x) => x.name.toLowerCase() === propertyName.toLowerCase() && x.value !== '')
      return item?.value
    },
    brandPriceHidden() {
      const hiddenBrands = this.siteSettings?.hiddenBrands ?? [];
      return hiddenBrands.map((str) => str.toLowerCase()).includes(this.getPropertyValue("brand")?.toLowerCase()) && !this.isLoggedIn
    },
    toggle(payload) {
      if (this.open.includes(payload)) {
        this.open = this.open.filter((item) => item !== payload)
      } else {
        this.open.push(payload)
      }
    },

    setSchema() {
      if (!this.product) return
      const valueMissing = 'Value missing'
      const product = this.product

      // Helper function to generate price specifications
      const createPriceSpec = (price, vatIncluded, description) => ({
        '@type': 'UnitPriceSpecification',
        'priceCurrency': product.price?.currencyCode || 'DKK',
        'price': price || valueMissing,
        'valueAddedTaxIncluded': vatIncluded,
        'description': description
      })

      // Extracting necessary product details with fallback values
      let schema = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        'name': product.title || valueMissing,
        'sku': product.sku || valueMissing,
        'description': product.longDescription || product.shortDescription || valueMissing,
        'image':
          product.images?.map((img) => {
            return `${process.env.umbracoBaseUrl}${img.url}`
          }) || [],
        'brand': {
          '@type': 'Brand',
          'name': product.properties?.find((p) => p.name === 'Brand')?.value || valueMissing
        },
        'additionalProperty':
          product.properties
            ?.map((p) => {
              if (!p.facet || p.value === '' || p.name === 'Assortments') return null
              return {
                '@type': 'PropertyValue',
                'name': p.name,
                'value': p.value + (p.unit ? ' ' + p.unit : '')
              }
            })
            .filter(Boolean) || [],
        'offers': {
          '@type': 'Offer',
          'url': `${process.env.baseURL}/produkt${product.url}` || valueMissing,
          'priceCurrency': product.price?.currencyCode || 'DKK',
          'price': product.price?.priceExVat || valueMissing,
          'itemCondition': 'https://schema.org/NewCondition',
          'availability': this.product.stockInfo?.actualAmount ?? 0 > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          'seller': {
            '@type': 'Organization',
            'name': 'Brønnum',
            'url': process.env.baseURL
          },
          'priceSpecification': [
            createPriceSpec(product.price?.regular, true, 'Original price including VAT'),
            createPriceSpec(product.price?.regularExVat, false, 'Original price excluding VAT')
          ]
        }
      }

      // If a discount exists, add discounted prices
      if (product.price?.discount > 0) {
        schema.offers.priceSpecification.unshift(
          createPriceSpec(product.price?.priceExVat, false, 'Offer price excluding VAT'),
          createPriceSpec(product.price?.price, true, 'Offer price including VAT')
        )
      }

      // Inject JSON-LD schema into <head>
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.text = JSON.stringify(schema)

      document.head.appendChild(script)
    }
  }
}
</script>
